/**
 * Sentry service — error tracking, performance traces, session replay,
 * and Pinia action breadcrumbs.
 *
 * # Toggle-controlled by design
 * All exported functions are no-ops unless BOTH conditions hold:
 *  1. `VITE_ENABLE_SENTRY === 'true'` — the dedicated on/off switch, set
 *     per-deploy (matches the existing `VITE_ENABLE_OBFUSCATION` convention).
 *  2. `VITE_SENTRY_DSN` is non-empty.
 *
 * The dual gate is defensive: if the toggle is enabled but the DSN is
 * blank, Sentry stays silent rather than failing at init. `isEnabled` is
 * computed once at module load — since both inputs are `import.meta.env.*`,
 * Vite inlines them at build time and constant-folds the boolean, so the
 * no-op branches are dead code in builds where Sentry is off.
 *
 * # Per-event environment label
 * The `environment` field on each Sentry event is read from `VITE_NODE_ENV`
 * (e.g. `dev`, `staging`, `production`). This is a label only — it does
 * NOT gate Sentry — used by the Sentry dashboard to filter events by
 * deploy environment.
 *
 * # What gets captured
 *  - Uncaught exceptions, Vue component errors, and unhandled promise
 *    rejections — registered automatically by `Sentry.init({ app })`.
 *  - HTTP request traces, sampled at 20% (`tracesSampleRate`). Trace headers
 *    are only attached to our own kwatt.id hosts (see `tracePropagationTargets`).
 *  - Session replays — 0% of healthy sessions, 100% of sessions that hit an
 *    error. All text is masked (`maskAllText`) and all media blocked
 *    (`blockAllMedia`) so replays carry no PII from the DOM. Network
 *    request/response bodies are captured ONLY for `GET /v1/transaction/{uuid}`
 *    (charging-detail) via `networkDetailAllowUrls`; every user-domain path
 *    (`/v1/user/*`) is explicitly denied via `networkDetailDenyUrls`.
 *  - Manually-captured WebSocket errors via {@link captureChargingError} —
 *    Socket.IO is not auto-instrumented by Sentry, so the charging telemetry
 *    component calls this helper explicitly.
 *  - Pinia store actions as breadcrumbs (via {@link attachSentryToPinia}) —
 *    state attachment is OFF for security; see the function docs.
 *
 * # Privacy posture
 *  - `sendDefaultPii` is left at its default (`false`) — Sentry will not
 *    capture cookies, the `Authorization` header, or the user IP unless we
 *    opt in.
 *  - `beforeSend` strips `Authorization` (case-insensitive) as defense-in-depth
 *    in case a future SDK change or fetch instrumentation surfaces the header.
 *    Note: `beforeSend` runs for Issue events ONLY — Replay network envelopes
 *    bypass it, so Replay's privacy boundary is its allow/deny URL lists.
 *  - Session replays mask all text, block all media, and capture network
 *    bodies only for the GET charging-detail endpoint (see init below).
 *  - User identification is opt-in per call (see {@link identifySentryUser}).
 *
 * # Usage
 *  - `initSentry(app, router)` — once, in `src/main.ts`, before
 *    `app.use(createPinia())` and `app.use(router)`.
 *  - `attachSentryToPinia(pinia)` — once, before `app.use(pinia)`.
 *  - `identifySentryUser(session)` — after a successful session fetch.
 *  - `clearSentryUser()` — on logout (already wired inside `authStore.logout()`).
 *  - `captureChargingError(error, context)` — at Socket.IO error sites.
 */

import type { App } from 'vue'
import type { Router } from 'vue-router'
import type { Pinia } from 'pinia'
import * as Sentry from '@sentry/vue'
import type { SessionData } from '@/utils/models/User'

/**
 * Compile-time-foldable flag. Vite inlines both env reads, so this becomes
 * a literal `true` or `false` in the production bundle, letting Rollup
 * tree-shake every guarded no-op branch.
 */
const isEnabled =
  import.meta.env.VITE_ENABLE_SENTRY === 'true' &&
  Boolean(import.meta.env.VITE_SENTRY_DSN)

/**
 * Initialize Sentry for the Vue app.
 *
 * Side effects:
 *  - Registers `app.config.errorHandler` to forward Vue component errors.
 *  - Installs `browserTracingIntegration` (with Vue Router instrumentation
 *    so navigations become transactions) and `replayIntegration`.
 *  - Begins capturing breadcrumbs (clicks, fetches, console, etc.).
 *
 * No-op when {@link isEnabled} is false.
 *
 * @param app    The Vue app instance from `createApp(App)`.
 * @param router The Vue Router instance — required for per-route transactions.
 */
export function initSentry(app: App, router: Router): void {
  if (!isEnabled) return

  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_NODE_ENV,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    integrations: [
      Sentry.browserTracingIntegration({ router }),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
        // Default-deny: all text stays masked except elements explicitly
        // marked `data-sentry-unmask` / `.sentry-unmask` — used only on the
        // PII-free charging-flow copy (status titles, error/dialog text,
        // button labels). Since SDK v8 these selectors are no longer included
        // by default, so they must be listed here. A descendant marked
        // `data-sentry-mask` (a default mask selector) re-masks embedded
        // figures inside an unmasked region (e.g. refund kWh amounts).
        unmask: ['.sentry-unmask', '[data-sentry-unmask]'],
        // Capture request/response bodies ONLY for `GET /v1/transaction/{uuid}`
        // (charging-detail). The regex requires uppercase-alphanumeric tokens
        // (UUID shape, e.g. "202605210451L0T5V") followed by a query/fragment
        // or URL end, which excludes the dictionary-word siblings under the
        // same namespace (`checkout`, `remote-start`, `cancel`, etc.). Every
        // unmatched URL stays at SDK-default metadata-only.
        //
        // `beforeSend` does NOT cover Replay network bodies — they ride a
        // separate envelope path with its own `beforeAddRecordingEvent` hook
        // (unused here). The allowlist is the only privacy boundary, hence
        // the tightness.
        networkDetailAllowUrls: [/\/v1\/transaction\/[A-Z0-9]{10,}(?:[?#]|$)/],
        // Defense-in-depth deny for every user-domain path. Deny takes
        // precedence over allow per SDK semantics, so a future broadening
        // of the allowlist cannot accidentally leak fullname / phone /
        // email / PIN / password endpoints.
        networkDetailDenyUrls: [/\/v1\/user(?:\/|$)/]
      })
    ],
    // Performance traces: 10% of page navigations / route transitions get a
    // full transaction with child spans. Enough sensitivity to spot p95/p99
    // regressions on common routes without doubling the quota burn.
    tracesSampleRate: 0.1,
    // Healthy-session replays: OFF. We never need to watch a session that
    // didn't produce an error; the quota would otherwise be dominated by
    // uninteresting traffic. Bump to 0.01 later if we ever want a baseline.
    replaysSessionSampleRate: 0,
    // Error-session replays: 100%. The replay integration keeps the last ~60s
    // buffered in memory and only uploads when an error fires, so this is
    // bounded by the error rate, not by traffic. High signal, low cost.
    replaysOnErrorSampleRate: 1,
    // Enables the structured Logs API (`Sentry.logger.*`). Opt-in per call;
    // no Logs-quota is spent until something explicit emits a log. Used for
    // high-signal business milestones via {@link logChargingEvent} — not
    // for console capture.
    enableLogs: true,
    // Attach `sentry-trace`/`baggage` headers only to requests bound for our
    // own backends (read from the same env vars the rest of the app uses to
    // reach them) plus localhost for local dev. Sentry matches string
    // targets as substrings, so each value covers any URL that contains it.
    // Auto-adapts across local / dev / staging / production with no code
    // change.
    tracePropagationTargets: [
      'localhost',
      import.meta.env.VITE_API_BASE_URL,
      import.meta.env.VITE_WS_API_BASE_URL
    ].filter(Boolean) as string[],
    beforeSend(event) {
      // Defense-in-depth: `sendDefaultPii: false` already prevents the
      // Authorization header from being captured, but if a future SDK change
      // or custom integration surfaces it, scrub it case-insensitively here.
      const headers = event.request?.headers as
        | Record<string, string>
        | undefined
      if (headers) {
        for (const key of Object.keys(headers)) {
          if (key.toLowerCase() === 'authorization') {
            delete headers[key]
          }
        }
      }
      return event
    }
  })
}

/**
 * Attach the Sentry Pinia plugin to a Pinia instance.
 *
 * Effect: every action dispatched on any store generates a Sentry breadcrumb
 * (`pinia.<storeId>/<action>`), so the next error report shows the trail of
 * store activity that led up to the crash.
 *
 * **Security: `attachPiniaState` is intentionally `false`.** Several stores
 * hold plaintext secrets that must never leave the browser:
 *  - `login-store` → `pin`, `confirmPin`
 *  - `change-password-store` → `currentPassword`
 *  - `register-store`, `edit-personal-store` → phone numbers and form fields
 *
 * Attaching state would serialize all of those into every event. The action
 * *names* alone are non-sensitive and remain enabled via `addBreadcrumbs`.
 * If we ever want richer state in breadcrumbs, add a per-store
 * `stateTransformer` that explicitly redacts sensitive keys — do NOT flip
 * `attachPiniaState` to `true`.
 *
 * No-op when {@link isEnabled} is false.
 *
 * @param pinia The Pinia instance from `createPinia()`, before `app.use(pinia)`.
 */
export function attachSentryToPinia(pinia: Pinia): void {
  if (!isEnabled) return

  pinia.use(
    Sentry.createSentryPiniaPlugin({
      attachPiniaState: false,
      addBreadcrumbs: true
    })
  )
}

/**
 * Identify the current user on the Sentry scope so subsequent events are
 * tagged with their identity, making it easy to filter "errors affecting
 * user X" in the dashboard.
 *
 * Called from `useTokenValidation.handleValidateToken()` after a successful
 * `GET_SESSION` response — that's the layer where {@link SessionData} (with
 * the internal `id`, `fullname`, and `phone`) is in scope.
 *
 * We deliberately pass only `id`, `username` (fullname), and `phone` — no
 * email, email-verification flag, or point balance. Identity is enough to
 * group events by user; the extra fields would just sit in Sentry without
 * adding investigative value.
 *
 * `phone` is mirrored as a top-level tag (`phone:<digits>`) because
 * Sentry's User panel only renders its curated schema fields (`id`,
 * `username`, `email`, `ip_address`); custom keys passed via `setUser` get
 * tucked away. As a tag it's first-class — visible on every event and
 * directly queryable via `phone:0812*`. Cleared in {@link clearSentryUser}.
 *
 * No-op when {@link isEnabled} is false.
 *
 * @param session The session payload returned by `API.CMS.GET_SESSION`.
 */
export function identifySentryUser(session: SessionData): void {
  if (!isEnabled) return

  Sentry.setUser({
    id: session.id,
    username: session.fullname,
    phone: session.phone
  })

  Sentry.setTag('phone', session.phone)
}

/**
 * Clear the Sentry user from the scope. Called from `authStore.logout()` so
 * the single store-level hook covers every logout path (manual sign-out,
 * 401, inactive account, maintenance).
 *
 * Also clears the `phone` tag set in {@link identifySentryUser} so it can't
 * leak onto the next user's events in the same browser tab.
 *
 * No-op when {@link isEnabled} is false.
 */
export function clearSentryUser(): void {
  if (!isEnabled) return
  Sentry.setUser(null)
  Sentry.setTag('phone', undefined)
}

/**
 * Manually report a WebSocket error from the charging telemetry component.
 * Socket.IO is not auto-instrumented by Sentry, so error sites must call
 * this explicitly.
 *
 * Tags the event with `source: 'websocket-charging'` so charging-related
 * issues can be filtered as a group in the Sentry dashboard, plus a finer
 * `context` tag to distinguish connection failures from message-handler bugs.
 *
 * No-op when {@link isEnabled} is false.
 *
 * @param error   The thrown value caught by the Socket.IO event handler.
 * @param context Where the error came from. `connect_error` means the socket
 *                failed to establish; `message_handler` means an exception
 *                escaped the per-message try/catch.
 */
export function captureChargingError(
  error: unknown,
  context: 'connect_error' | 'message_handler'
): void {
  if (!isEnabled) return

  Sentry.captureException(error, {
    tags: {
      source: 'websocket-charging',
      context
    }
  })
}

// ------ Structured logs (charging-session timeline) ------

/**
 * Module-scoped store for intent timestamps. Used by {@link logChargingEvent}
 * with the `markIntent` / `consumeIntent` options to attach `duration_ms` to
 * ack-side log events. Bounded by browser-tab lifetime; a leaked entry from a
 * user closing the tab between intent and ack is harmless.
 */
const intentTimestamps = new Map<string, number>()

/**
 * Known business `errorCode`s from `/checkout` and remote-start endpoints.
 * Used by {@link classifyCheckoutFailure} so expected, user-recoverable
 * outcomes (e.g. inactive account, charger in problem) land at `level:warn`,
 * leaving `level:error` for genuine bugs and network failures.
 */
const KNOWN_CHECKOUT_ERROR_CODES = new Set<string>([
  'TRANSACTION_DRIVER_NOT_ACTIVE',
  'TRANSACTION_REMOTE_START_NOT_PREPARING',
  'TRANSACTION_REMOTE_START_ERROR',
  'TRANSACTION_CHARGER_CANNOT_USE',
  'TRANSACTION_REMOTE_START_CHARGER_IN_PROBLEM',
  'TRANSACTION_REMOTE_STOP_ERROR'
])

/**
 * Pick a Sentry log level for a charging-flow failure based on its `errorCode`.
 * Known business codes → `'warn'`; anything else (network, unknown, parse) →
 * `'error'`.
 */
export function classifyCheckoutFailure(
  errorCode: string | undefined
): 'warn' | 'error' {
  return errorCode && KNOWN_CHECKOUT_ERROR_CODES.has(errorCode)
    ? 'warn'
    : 'error'
}

/**
 * Record a structured business-event log for the charging-session timeline.
 *
 * Distinct from {@link captureChargingError}, which reports exceptions to the
 * Issues tab. These rows land in the dashboard's **Logs** tab and are meant
 * for traceable milestones — `charging.checkout.requested`,
 * `charging.stop.confirmed` — not exceptions and not per-frame telemetry.
 *
 * Filter all rows for one session with `sessionId:<uuid>` in the Logs tab.
 * For rows that fire *before* the session UUID is issued (i.e. before
 * `charging.checkout.initiated`), filter on `chargerId:<id>` plus a time
 * window instead.
 *
 * @param event       Dot-namespaced past-tense event name (e.g.
 *                    `charging.start.requested`). Used as the row's primary
 *                    search key — keep stable, vary detail via attributes.
 * @param attributes  Searchable key/value attrs. Internal IDs and enum
 *                    values only — same PII posture as
 *                    {@link identifySentryUser}.
 * @param options     `level` defaults to `'info'`. `markIntent` stamps
 *                    `Date.now()` against a key; a later call with
 *                    `consumeIntent` for the same key auto-attaches
 *                    `duration_ms`, capturing intent→ack latency without
 *                    coupling the two call sites.
 *
 *                    **`captureError` + Session Replay:** when `level` is
 *                    `'error'`, this also captures a Sentry *exception* event
 *                    (not just a log), which is what flushes the buffered
 *                    Session Replay (`replaysOnErrorSampleRate: 1`). The Logs
 *                    API alone never flushes a replay — it rides a separate
 *                    envelope. Pass the caught error as `captureError` to
 *                    preserve its stack/grouping; if omitted (e.g. a silent
 *                    timeout with no thrown error) a synthetic `Error(event)`
 *                    is captured instead. `'warn'`/`'info'` events stay
 *                    logs-only and never produce a replay — so known
 *                    business failures (classified `'warn'` by
 *                    {@link classifyCheckoutFailure}) add no replay noise.
 *
 * No-op when {@link isEnabled} is false.
 */
export function logChargingEvent(
  event: string,
  attributes: Record<string, string | number | boolean> = {},
  options: {
    level?: 'info' | 'warn' | 'error'
    markIntent?: string
    consumeIntent?: string
    captureError?: unknown
  } = {}
): void {
  if (!isEnabled) return

  if (options.markIntent) {
    intentTimestamps.set(options.markIntent, Date.now())
  }

  const finalAttributes: Record<string, string | number | boolean> = {
    ...attributes
  }

  if (options.consumeIntent) {
    const startedAt = intentTimestamps.get(options.consumeIntent)
    if (startedAt !== undefined) {
      finalAttributes.duration_ms = Date.now() - startedAt
      intentTimestamps.delete(options.consumeIntent)
    }
  }

  const level = options.level ?? 'info'
  if (level === 'warn') {
    Sentry.logger.warn(event, finalAttributes)
  } else if (level === 'error') {
    Sentry.logger.error(event, finalAttributes)
  } else {
    Sentry.logger.info(event, finalAttributes)
  }

  // Error-level events ALSO capture an exception so the buffered Session
  // Replay flushes — the Logs API above never triggers a replay on its own.
  // Use the real caught error when we have one (preserves stack + grouping);
  // otherwise synthesize one so no-throw failures (e.g. a stuck-after-payment
  // timeout) still upload a replay. The event name and attributes ride along
  // as context to keep the Issue self-describing.
  if (level === 'error') {
    const cause =
      options.captureError !== undefined ? options.captureError : new Error(event)
    Sentry.captureException(cause, {
      level: 'error',
      tags: { source: 'charging-flow' },
      extra: { event, ...finalAttributes }
    })
  }

  recordChargingMetrics(event, finalAttributes)
}

// ------ Metrics (aggregate dashboards) ------

/**
 * Attribute keys that are safe to use as metric dimensions — bounded enums
 * and booleans whose value space is small and stable. Everything else
 * (`sessionId`, `chargerId`, `connectorId`, `uuid`, free-form error messages,
 * numeric durations) is dropped by {@link pickMetricDimensions} before it
 * reaches `Sentry.metrics.*`.
 *
 * Sentry rate-limits and bills metrics by unique dimension combinations, so
 * leaking any per-session field here would explode the bill while producing
 * dashboards no human can read. Default-deny: new attribute keys must be
 * added here explicitly to become metric dimensions.
 */
const METRIC_DIMENSION_KEYS = new Set<string>([
  // Bounded string enums
  'errorCode', // KNOWN_CHECKOUT_ERROR_CODES + QR error codes (~15 values)
  'from', // Status enum + 'INITIAL' sentinel
  'to', // Status enum
  'chargingStatus',
  'lastStatus',
  'source', // 'fetch' | 'websocket' (charging-detail.store)
  'dialog', // FAULT_DIALOGS + non-fault dialog ids
  'trigger', // DialogTrigger union (5 values)
  'action', // DialogAction union (5 values) — which button resolved a dialog
  'reason', // RedirectReason enum (~6 values)
  'from_route',
  'to_route',
  'failure_source', // 'api_status_false' | 'exception' | 'unexpected_payment_status'
  'endpoint',
  'paymentStatus',
  'paymentMethodType',
  // Booleans
  'hasPriorStoreState',
  'hasRemoteStartFired',
  'isRemoteStart', // backend verdict, unlike hasRemoteStartFired (client-side)
  'isUsingKwhBalance'
])

function pickMetricDimensions(
  attributes: Record<string, string | number | boolean>
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {}
  for (const key of Object.keys(attributes)) {
    if (METRIC_DIMENSION_KEYS.has(key)) {
      out[key] = attributes[key]
    }
  }
  return out
}

/**
 * Mirror a charging-flow log event into Sentry Metrics so the same business
 * vocabulary that powers the Logs tab also powers aggregate dashboards
 * (funnel rates, p95 latencies, error counts by code).
 *
 * Emits up to three metrics per call:
 *  - `charging.event` (counter) — always. Universal funnel/page-view counter
 *    keyed by `event`; filter as `event:charging.checkout.requested` etc.
 *  - `charging.event.duration` (distribution, ms) — when `attributes.duration_ms`
 *    is present (i.e. the call site used the `markIntent`/`consumeIntent`
 *    pair). Gives p50/p95/p99 latency per funnel step for free.
 *  - `charging.error` (counter) — when `attributes.errorCode` is a non-empty
 *    string. Separated from `charging.event` so error-rate alerts can target
 *    a single metric without filter expressions.
 *
 * Cardinality protection: only keys in {@link METRIC_DIMENSION_KEYS} flow
 * through to metric attributes. `sessionId`, `chargerId`, `connectorId`,
 * `uuid`, `errorMessage`, `duration_ms`, `httpStatus` are intentionally dropped.
 */
function recordChargingMetrics(
  event: string,
  attributes: Record<string, string | number | boolean>
): void {
  const dims = pickMetricDimensions(attributes)

  Sentry.metrics.count('charging.event', 1, {
    attributes: { event, ...dims }
  })

  if (typeof attributes.duration_ms === 'number') {
    Sentry.metrics.distribution(
      'charging.event.duration',
      attributes.duration_ms,
      {
        unit: 'millisecond',
        attributes: { event, ...dims }
      }
    )
  }

  if (typeof attributes.errorCode === 'string' && attributes.errorCode !== '') {
    Sentry.metrics.count('charging.error', 1, {
      attributes: { event, errorCode: attributes.errorCode }
    })
  }
}

/**
 * Dialog identifiers that map to user-visible error/fault states. Used by
 * {@link notifyDialogShown} to pick `warn` vs `info` level automatically —
 * keeps `level:error` filter in the dashboard actionable while still
 * flagging fault-class dialogs in `level:warn`.
 */
const FAULT_DIALOGS = new Set<string>([
  'charger_faulted',
  'charger_suspended',
  'vehicle_stopped',
  'cable_not_connected',
  'charging_error',
  'unable_to_charge'
])

/**
 * Why a dialog appeared. `api_error` = a POST returned an errorCode;
 * `activity_watch` = transactionActivity.type flipped to a fault/suspend
 * value; `status_watch` = chargingStatus reached a terminal-but-bad value;
 * `computed` = derived expression became truthy; `user_action` = an
 * explicit button (the "Stop" button, etc.).
 */
export type DialogTrigger =
  | 'api_error'
  | 'activity_watch'
  | 'status_watch'
  | 'computed'
  | 'user_action'

/**
 * Emit a `charging.dialog.shown` log row when a user-facing dialog opens.
 * Centralized so all dialog setters across the charging-start and
 * charging-detail stores route through the same shape. The dashboard query
 * `event:charging.dialog.shown dialog:<x>` then enumerates every occurrence
 * regardless of which code path raised it.
 *
 * @param dialog   Stable dialog identifier (e.g. `'charger_faulted'`).
 *                 If in {@link FAULT_DIALOGS}, the event is logged at
 *                 `warn`; otherwise `info`.
 * @param trigger  Where the dialog came from. Helps disambiguate cases
 *                 where the same dialog can be raised by multiple sources
 *                 (e.g. `charger_faulted` from an API error vs. an
 *                 activity-watch transition to `FAULTED`).
 * @param extras   Additional attributes — `sessionId`, `chargerId`,
 *                 `connectorId`, `chargingStatus`, `errorCode`. Pass as
 *                 many as the call site has in scope.
 */
export function notifyDialogShown(
  dialog: string,
  trigger: DialogTrigger,
  extras: Record<string, string | number | boolean> = {}
): void {
  logChargingEvent(
    'charging.dialog.shown',
    { dialog, trigger, ...extras },
    { level: FAULT_DIALOGS.has(dialog) ? 'warn' : 'info' }
  )
}

/**
 * What the user did to resolve a dialog. Pairs with {@link notifyDialogShown}:
 * `shown` tells us a fault/error surfaced, `resolved` tells us which recovery
 * path the user chose. Together they expose the recovery funnel — e.g. how
 * often `charger_faulted` is followed by `retry` vs `stop`.
 *
 *  - `retry`      — user asked to try again ("Coba Lagi").
 *  - `cancel`     — user abandoned the session ("Batalkan").
 *  - `stop`       — user stopped an active charge.
 *  - `connected`  — user confirmed they plugged the cable in.
 *  - `dismiss`    — user closed the dialog without choosing an action.
 *  - `continue`   — user resumed an existing session ("Lanjutkan Charging").
 *  - `create_new` — user started over instead of resuming
 *                   ("Buat Sesi Charging Baru").
 */
export type DialogAction =
  | 'retry'
  | 'cancel'
  | 'stop'
  | 'connected'
  | 'dismiss'
  | 'continue'
  | 'create_new'

/**
 * Emit a `charging.dialog.resolved` log row when the user acts on a dialog.
 * Always `info` — the resolution itself is not an error (the originating
 * fault was already captured by {@link notifyDialogShown} / the failing call).
 *
 * @param dialog  Stable dialog identifier — must match the `dialog` value
 *                passed to {@link notifyDialogShown} so the two rows join.
 * @param action  Which button the user pressed (see {@link DialogAction}).
 * @param extras  Additional attributes — `sessionId`, `chargerId`,
 *                `connectorId`, `chargingStatus`, `connectorStatus`.
 */
export function notifyDialogResolved(
  dialog: string,
  action: DialogAction,
  extras: Record<string, string | number | boolean> = {}
): void {
  logChargingEvent(
    'charging.dialog.resolved',
    { dialog, action, ...extras },
    { level: 'info' }
  )
}
